import Title from 'antd/lib/typography/Title';
import { Button, Divider, Form, Space, Typography } from 'antd';
import "react-big-calendar/lib/css/react-big-calendar.css";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import { useCreateEvent } from '../../Hooks/useCreateEvent';
import { useLocation, useNavigate } from "react-router-dom";
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import moment from 'moment'
import 'moment-timezone'
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { GoogleService } from '../../Services/GoogleService';
import { CalenderSource } from '../../Types/CalendarEvent';
import BookyBox from '../../Components/Layout/BookyBox';
import { BookyCustomInput } from '../../Components/Forms/BookyCustomInput';
import { GoogleTokenProvider, GoogleAuthenticationScope } from '../../Providers/GoogleTokenProvider';
import { useAuth } from '../../Contexts/AuthContext';
import PageWide from '../../Components/Page/PageWide';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { EventDetailsFields, EventSettingsFields, EventTimesFields } from '../../Components/Forms/EventFormSections';
import { EventType } from '../../Types/EventType';
import { AnswerOptions } from '../../Types/AnswerOptions';
import PageHelp from '../../Components/Page/PageHelp';
import Paragraph from 'antd/lib/typography/Paragraph';
import { getDeadlineError, getFirstStart } from '../../Helpers/EventDateValidation';

const Create = () => {

  const { events, step, changeStep, CreateEventItem, UpdateEvent, DeleteEvent, CreateEvent, timeZone, setTimeZone, LoadTimeZones, timeZones, SetFullDayOnEvent, setMaxParticipants, maxParticipants, allowWaitlist, setAllowWaitlist, maxWaitlist, setMaxWaitlist, membersOnly, setMembersOnly, showOnMemberSite, setShowOnMemberSite, eventType, setEventType, timeChoice, setTimeChoice, AddTime, deadline, setDeadline, displayType, setDisplayType, automaticReminder, setAutomaticReminder, automaticReminderDays, setAutomaticReminderDays, SetGoogleEvents, customStep, setCustomStep } = useCreateEvent();
  const navigation = useNavigate();
  const location = useLocation();
  // Set when the event is started from the chosen time of a poll.
  const prefill = (location.state as any)?.prefill;
  const prefillApplied = useRef(false);
  const isPoll = eventType === EventType.Poll;
  const [startDate, setStartDate] = useState("" as string);
  const [endDate, setEndDate] = useState("" as string);
  const [googleToken, setGoogleToken] = useState("" as string);
  const [showGoogleCalender, setShowGoogleCalender] = useState(false as boolean);
  const { user } = useAuth();
  const [form] = Form.useForm()
  const [show24hrsFormat, setShow24hrsFormat] = useState(false as boolean);
  const GetText = TextService.GetText;

  useEffect(() => {
    LoadTimeZones();

    if (getCurrentLanguage() === "se") {
      setShow24hrsFormat(true);
    }

    // The effect can run twice while developing, and the time must only be added once.
    if (prefill && !prefillApplied.current) {
      prefillApplied.current = true;
      form.setFieldsValue({
        name: prefill.name,
        eventDescription: prefill.description,
        eventLocation: prefill.location,
      });
      AddTime(new Date(prefill.start), new Date(prefill.end), prefill.isFullDay === true);
      // Shows the week of the time in the calendar.
      setStartDate(moment(prefill.start).startOf('week').toISOString());
      setEndDate(moment(prefill.start).endOf('week').toISOString());
    }
  }, [])

  const OnCancelClick = async () => {
    navigation("/" + getCurrentLanguage() + "/myevents/");
  }

  // At least one time must be chosen. The error shows first when the organizer tries to create the event.
  const [showTimesError, setShowTimesError] = useState(false);
  const timesRef = useRef<HTMLDivElement>(null);
  const hasTimes = events.some(item => item.source === CalenderSource.EventBooky);

  const checkTimes = () => {
    setShowTimesError(!hasTimes);
    return hasTimes;
  }

  // The missing fields can be far up on the page, so a hint is shown by the create button until they are filled in.
  const [hasFieldErrors, setHasFieldErrors] = useState(false);
  const showMissingHint = hasFieldErrors || (showTimesError && !hasTimes);

  const onFinishFailed = () => {
    checkTimes();
    setHasFieldErrors(true);
  }

  const onFieldsChange = () => {
    if (hasFieldErrors) {
      setHasFieldErrors(form.getFieldsError().some(field => field.errors.length > 0));
    }
  }

  // Shown as soon as the deadline is chosen, and the event can't be created while it is wrong.
  const deadlineRef = useRef<HTMLDivElement>(null);
  const deadlineError = getDeadlineError(deadline, events.filter(item => item.source === CalenderSource.EventBooky), true);

  const FormCreateClick = async (formValues: any) => {
    if (!checkTimes()) {
      timesRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (deadlineError) {
      deadlineRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const newlyCreatedEvent = await CreateEvent(formValues);
    navigation("/" + getCurrentLanguage() + "/myevents/" + newlyCreatedEvent.uniqueId + "/design?iscreated=true");
  }

  // Enter in a field must not create the event, e.g. when choosing a place in the location field.
  // The event is only created with the button. Enter still makes a new line in the description.
  const onFormKeyDown = (e: KeyboardEvent<HTMLFormElement>) => {
    const target = e.target as HTMLElement;
    if (e.key === "Enter" && target.tagName !== "TEXTAREA" && target.tagName !== "BUTTON") {
      e.preventDefault();
    }
  }

  const getEventBookyEvents = () => {
    return events
      .filter(item => item.source === CalenderSource.EventBooky);
  }

  // The participant list is hidden by default for both a poll and a sign-up.
  const onDisplayTypeChange = (value: number) => {
    setDisplayType(value);
  }

  const onEventTypeChange = (value: EventType) => {
    setEventType(value);
    // The answer options of a poll and a sign-up are different, so each type starts with its own default.
    form.setFieldValue("answerOptions", value === EventType.Poll ? AnswerOptions.YesNoMaybe : AnswerOptions.YesNo);
  }

  const onNavigate = (date: any) => {
    setStartDate(moment(date).startOf('week').toISOString());
    setEndDate(moment(date).endOf('week').toISOString());

    if (googleToken != null) {
      LoadGoogleEvents(googleToken, moment(date).startOf('week').toISOString(), moment(date).endOf('week').toISOString());
    }

  }

  const AuthenticateGoogle = async () => {
    if (googleToken) {
      if (!startDate) {
        LoadGoogleEvents(googleToken, moment().startOf('week').toISOString(), moment().endOf('week').toISOString());
      }
      else {
        LoadGoogleEvents(googleToken, startDate, endDate);
      }
    }
    else {
      GoogleTokenProvider.GetToken((response: any) => {
        setGoogleToken(response.access_token);
        if (!startDate) {
          LoadGoogleEvents(response.access_token, moment().startOf('week').toISOString(), moment().endOf('week').toISOString());
        }
        else {
          LoadGoogleEvents(response.access_token, startDate, endDate);
        }
      }, GoogleAuthenticationScope.CalendarReadOnly);
    }
  }

  const LoadGoogleEvents = async (token: string, start: string, end: string) => {
    if (!token) {
      return;
    }
    var events = await GoogleService.GetGoogleCalenderEvents(token, start, end);
    SetGoogleEvents(events.items);
    setShowGoogleCalender(true);
  }

  const RemoveGoogleEvents = () => {
    setShowGoogleCalender(false);
    SetGoogleEvents([]);
  }

  const isPremium = () => {
    return user?.subscriptionLevel != SubscriptionLevel.Free
  }

  return <PageWide>
    <Form
      form={form}
      data-testid="create-form"
      name="basic"
      onFinish={FormCreateClick}
      onFinishFailed={onFinishFailed}
      onFieldsChange={onFieldsChange}
      onKeyDown={onFormKeyDown}
      layout="vertical"
      initialValues={{
        remember: true,
        // A new event is a sign-up with "Kommer" and "Kommer inte".
        answerOptions: AnswerOptions.YesNo,
      }}
      autoComplete="off"
    >
      <BookyBox>
        {/* The help button sits at the right edge of the title row, the title stays centered. */}
        <div style={{ position: "relative", width: "100%", textAlign: "center" }}>
          <PageHelp>{GetText("Create_HowItWorks").split("\n\n").map((text, i) => <Paragraph key={i}>{text}</Paragraph>)}</PageHelp>
          <Title>{GetText("Create_CreateEvent")}</Title>
        </div>
        <Divider className="BookyTitleWithIconDivider" />
        <EventDetailsFields eventType={eventType} onEventTypeChange={onEventTypeChange} defaultLocation={prefill?.location}
          onLocationSelected={(place: any) => { form.setFieldValue("eventLocation", place) }} />
      </BookyBox>

      <BookyBox>
        <div ref={timesRef} />
        <EventTimesFields step={step} onStepChange={changeStep} customStep={customStep} onCustomStepChange={setCustomStep}
          calendarEvents={events} times={getEventBookyEvents()} startDate={startDate} onNavigate={onNavigate}
          CreateEventItem={CreateEventItem} UpdateEvent={UpdateEvent} DeleteEvent={DeleteEvent} SetFullDayOnEvent={SetFullDayOnEvent}
          show24hrsFormat={show24hrsFormat} timeZone={timeZone} timeZones={timeZones} onTimeZoneChange={setTimeZone}
          error={showTimesError && !hasTimes ? GetText("Create_Times_Required") : undefined} />
      </BookyBox>

      <BookyBox alignLeft={true}>
        <div ref={deadlineRef} />
        <EventSettingsFields isPoll={isPoll} isPremium={isPremium()}
          onAnswerOptionsChange={(value) => form.setFieldValue("answerOptions", value)}
          deadline={deadline} onDeadlineChange={setDeadline} deadlineError={deadlineError} firstStart={getFirstStart(getEventBookyEvents())}
          maxParticipants={maxParticipants} onMaxParticipantsChange={setMaxParticipants}
          membersOnly={membersOnly} setMembersOnly={setMembersOnly} showOnMemberSite={showOnMemberSite} setShowOnMemberSite={setShowOnMemberSite}
          allowWaitlist={allowWaitlist} setAllowWaitlist={setAllowWaitlist} maxWaitlist={maxWaitlist} setMaxWaitlist={setMaxWaitlist}
          timeChoice={timeChoice} setTimeChoice={setTimeChoice} timesCount={getEventBookyEvents().length}
          displayType={displayType} onDisplayTypeChange={onDisplayTypeChange}
          automaticReminder={automaticReminder} setAutomaticReminder={setAutomaticReminder}
          automaticReminderDays={automaticReminderDays} setAutomaticReminderDays={setAutomaticReminderDays} />

        {!isPremium() ?
          <BookyCustomInput label="Ads" helpText={GetText("Create_NoAds")}>
            <span className='pro-link' style={{ justifyContent: 'left', display: 'flex' }}><a href={"/" + getCurrentLanguage() + "/Pricing"}>{GetText("Common_UpgradeToPremium")}</a> {GetText("Create_AutomaticReminder_Premium_ToRemoveAds")}</span>
          </BookyCustomInput> : ""}
        {user?.id != null ?
          <BookyCustomInput>
            <Space>
              <Button data-testid="submit-button" size="middle" type="default" onClick={OnCancelClick}>
                {GetText("Common_Cancel")}
              </Button>

              <Button data-testid="submit-button" size="middle" type="primary" htmlType="submit">
                {GetText("Create_CreateEvent")}
              </Button>
            </Space>
            {showMissingHint ? <div><Typography.Text type="danger" data-testid="create-missing-fields">{GetText("Create_MissingRequiredFields")}</Typography.Text></div> : null}
          </BookyCustomInput> : <Space>{GetText("Create_LoginToCreate")} <a href='/CreateUser'>{GetText("Create_CreateNewAccount")}</a></Space>}
      </BookyBox>
    </Form>
  </PageWide >
};

export default Create;